import qs from 'qs';
import fileDownload from 'react-file-download';
import auth from '~/auth';

import { Order } from '@luxuryescapes/contract-svc-order';

import { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import { EnrichedRefundMetadata, RefundMetadata } from '~/components/Refund/types';
import { ICard, ICardResponse } from '~/components/VirtualCreditCards/OrderDetail/OrderDetailPage';

import { SuppliersCodes } from '~/consts/bedbank';
import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';
import { OPERATION_SOFT_DELETE } from '~/consts/notes';
import { ITEM_TYPE_EXPERIENCE, OPERATION_CANCEL_CUSTOM_ORDER_ADMIN } from '~/consts/order';
import { OrderAccessRole } from '~/consts/orderAccess';
import { CollectionReasonKey } from '~/consts/refund';

import type { OrderItemLogsResponse } from '~/types/responses';
import type {
  CogsAdjustmentRequestResponse,
  CogsAdjustmentRequestsResponse,
  CreateCogsAdjustmentRequestBody,
  GetCogsAdjustmentRequestsQuery,
  UpdateCogsAdjustmentRequestBody,
} from '~/types/services/cogsAdjustmentRequest';

import { canViewVccDetails } from '~/utils/adminPermission';
import { formatDynamicAmountWithDollarType } from '~/utils/currencyFormatter';
import isUUID from '~/utils/isUUID';

import BedbankService from './BedbankService';
import {
  businessTravellerProxyAdminRefund,
  businessTravellerProxyGetAdminRefund,
} from './BusinessTraveller/BusinessTravellerService';
import { fetchInsuranceClaim } from './InsuranceService';
import offersService from './OffersService';
import {
  getDepositDetails,
  getMerchantFeeDetails,
  getPaymentPlans,
  getPayments,
  getRefundMeta,
  getReserveForZeroDetails,
} from './PaymentsService';
import ReservationService from './ReservationService';
import { dateNow, formatDateISO } from './TimeService';
import { request } from './common';

const FLIGHT_CANCELLATION_REASON = 'Change of Mind Within Refund Guarantee Period';
const FLIGHT_CANCELLATION_COMMENT = 'Self-serve by customer';

function getBaseURL() {
  return window.configs.API_HOST + '/api';
}

function dataRequest(path, data, method) {
  return request(path, {
    method,
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
}

function postRequest(path, data) {
  return dataRequest(path, data, 'POST');
}

function patchRequest(path, data) {
  return dataRequest(path, data, 'PATCH');
}

function putRequest(path, data) {
  return dataRequest(path, data, 'PUT');
}

export async function submitFlightCancellation(orderId: string, itemId: string, transactionKey: string) {
  const baseURL = getBaseURL();

  return putRequest(`${baseURL}/orders/${orderId}/items/${itemId}`, {
    op: 'refund',
    transaction_key: transactionKey,
    reason: FLIGHT_CANCELLATION_REASON,
    comment: FLIGHT_CANCELLATION_COMMENT,
    method: 'back_to_origin',
  });
}

export async function createOrder(data) {
  const baseURL = getBaseURL();

  return postRequest(`${baseURL}/orders`, data);
}

export interface PnrImportFlightItem {
  type: 'flight';
  pnr: string;
  transaction_key: string;
  price: number;
  /** The handling fee for each seat and extra baggage on the PNR, by Sabre ancillary id; the price includes them. */
  ancillary_handling_fees?: Array<{ ancillary_id: string; amount: number }>;
}

export type PnrImportServiceFeeItem = App.ServiceFeeOrderItemPayload;

/** Marks the imported PNRs as already paid, so the order takes no payment; the reference is free text. */
export interface PnrImportPaid {
  reference?: string;
}

/** Creates one order from one or more PNRs booked outside the platform, unpaid unless marked as paid. */
export interface PnrImportOrderRequest {
  transaction_key: string;
  brand: string;
  region_code: string;
  currency_code: string;
  customer_id: string;
  business_id?: string;
  paid?: PnrImportPaid;
  items: Array<PnrImportFlightItem | PnrImportServiceFeeItem>;
}

/** A PNR the import could not use, as reported by svc-order. */
export interface PnrImportFailure {
  pnr: string;
  status?: number;
  message: string;
}

export async function createPnrImportOrder(data: PnrImportOrderRequest): Promise<Order.Order> {
  const baseURL = getBaseURL();

  const response = await postRequest(`${baseURL}/orders/import-pnr`, data);
  return response.result;
}

/** Cancels an unpaid imported PNR order and releases its PNRs so they can be imported again. */
export async function cancelPnrImportOrder(orderId: string): Promise<void> {
  const baseURL = getBaseURL();

  await postRequest(`${baseURL}/orders/${orderId}/import-pnr/cancel`, {});
}

export function createItem(orderId, data) {
  const baseURL = getBaseURL();

  return postRequest(`${baseURL}/orders/${orderId}/items`, data);
}

export function finalizeOrder(orderId) {
  const baseURL = getBaseURL();
  const data = { op: 'finalize' };
  return patchRequest(`${baseURL}/orders/${orderId}`, data);
}

export function updateOrder(orderId, data) {
  if (orderId && data) {
    const baseURL = getBaseURL();
    return patchRequest(`${baseURL}/orders/${orderId}`, data);
  }
}

export type RegenerateOrderPinResponse = {
  pin: string;
};

export function regenerateOrderPin(orderId: string): Promise<RegenerateOrderPinResponse> {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/${orderId}/pin-regenerations`, { surface: 'admin' });
}

export type RecordOrderPinValidationOutcome = 'success' | 'failure';

export type RecordOrderPinValidationParams = {
  orderId: string;
  outcome: RecordOrderPinValidationOutcome;
};

export function recordOrderPinValidation({ orderId, outcome }: RecordOrderPinValidationParams): Promise<unknown> {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/${orderId}/pin-validations`, { outcome });
}

export function migrateOrderToLebt(orderId: string, businessId: string) {
  const baseURL = getBaseURL();
  return patchRequest(`${baseURL}/orders/${orderId}`, {
    op: 'migrate_brand',
    brand: 'lebusinesstraveller',
    utm_source: 'lebusinesstraveller',
    le_label: 'lebusinesstraveller',
    le_attribution: 'lebusinesstraveller',
    business_id: businessId,
  });
}

export function getSpecialRequestsOrderItems(page: number, perPage: number) {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/items?page=${page}&limit=${perPage}&op=list_special_requests_by_vendor`);
}

export function createCogsAdjustmentRequest(
  data: CreateCogsAdjustmentRequestBody,
): Promise<CogsAdjustmentRequestResponse> {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/cogs-adjustment-request`, data);
}

export function getCogsAdjustmentRequestByOrderItem(
  orderId: string,
  itemId: string,
): Promise<CogsAdjustmentRequestResponse> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/cogs-adjustment-request/order/${orderId}/item/${itemId}`);
}

export function getCogsAdjustmentRequests(
  params: GetCogsAdjustmentRequestsQuery,
  signal?: AbortSignal,
): Promise<CogsAdjustmentRequestsResponse> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/cogs-adjustment-request?${qs.stringify(params)}`, { signal });
}

export function updateCogsAdjustmentRequest(
  cogsAdjustmentRequestId: string,
  data: UpdateCogsAdjustmentRequestBody,
): Promise<CogsAdjustmentRequestResponse> {
  const baseURL = getBaseURL();
  return patchRequest(`${baseURL}/orders/cogs-adjustment-request/${cogsAdjustmentRequestId}`, data);
}

function getBookingNumbers(purchases) {
  for (let i = 0; i < purchases.length; i++) {
    purchases[i]['booking_numbers'] = purchases[i].items.map((item) => item.booking_number).join(', ');
  }
}

function restoreOrderId(purchases) {
  for (let i = 0; i < purchases.length; i++) {
    if (!purchases[i]['id_orders']) {
      purchases[i]['id_orders'] = purchases[i]['id'];
    }
  }
}

function isOrderId(input) {
  return /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/.test(input);
}

function isOfferId(input) {
  return /^[0-9a-zA-Z]{18}$/.test(input);
}

/**
 * Experience offer id has a prefix of 3 letters followed by a colon and then the offer id
 * e.g. "EXP:1234567890"
 */
function isExperienceOfferId(input) {
  return /^[A-Za-z]{3}:[^:]+$/.test(input);
}

interface GetPurchasesQueryParams {
  filterBy?: string;
  page: number;
  per_page: number;
  brand: string;
  customer_id: string;
  start_date?: string;
  end_date?: string;
  q?: string;
  traveller_name?: string;
  note_comment?: string;
  item_type?: string;
  item_types?: [string];
  type?: string;
  departure_id?: string;
  sortBy?: string;
  business_id?: string | number;
  property?: string;
  property_field?: string;
  include_shared_orders?: boolean;
  include_gift_search?: boolean;
  tour_source?: string;
  external_provider_id?: string;
  provider_order_id?: string;
  ibs_member_id?: string;
  multi_brand?: boolean;
  accommodation_v2_scope?: AccommodationV2Scope;
}

export type AccommodationV2Scope = 'all' | 'native' | 'shim';

export interface GetPurchasesOptions {
  page: number;
  per_page: number;
  brand: string;
  customer_id?: string;
  filterBy?: string;
  filterValue?: string;
  additionalFilter?: MultiFilterState;
  filter?: { [key: string]: string | Array<string> };
  sort?: { [key: string]: 'asc' | 'desc' };
  startDate?: string;
  endDate?: string;
  item_type?: string | Array<string>;
  offer_type?: string;
  businessId?: string | number;
  includeSharedOrders?: boolean;
  /** Opt in to GET /orders?multi_brand=true. Never sent for LEBT; otherwise defaults to true for lookups. */
  multiBrand?: boolean;
  /** Only with item_type=accommodationV2; svc-order defaults to native, which excludes shim shadows. */
  accommodationV2Scope?: AccommodationV2Scope;
  signal?: AbortSignal;
}

type SearchOptions = {
  brand?: string;
  page?: number;
  perPage?: number;
  orderBy?: string;
  orderDirection?: string;
  searchId: string;
};

type MultiSearchOptions = {
  page: number;
  perPage: number;
  orderBy: string;
  orderDirection: string;
  additionalFilter?: MultiFilterState;
};

export async function multiSearch({ page, perPage, orderBy, orderDirection, additionalFilter }: MultiSearchOptions) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/multi-search?`;

  const queryParams = {
    page,
    perPage,
    itemType: additionalFilter.itemType,
    offerId: additionalFilter.offerId,
    checkInDate: additionalFilter.checkInDate,
    checkOutDate: additionalFilter.checkOutDate,
    orderDirection,
    orderBy,
  };

  const purchases = await request(`${url}${qs.stringify(queryParams)}`);

  getBookingNumbers(purchases.result);

  return purchases;
}

export async function searchPurchases(
  { brand, page, perPage, orderBy, orderDirection, searchId }: SearchOptions,
  requestOptions?: RequestInit,
) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/search?`;

  const queryParams = {
    brand,
    page,
    perPage,
    searchId,
    orderDirection,
    orderBy,
  };

  const purchases = await request(`${url}${qs.stringify(queryParams)}`, requestOptions);

  // collect all booking numbers per order
  getBookingNumbers(purchases.result);

  return purchases;
}

export async function flightPNRSearch(pnrId: string) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/pnr-search/${pnrId}`;

  const purchases = await request(url);

  getBookingNumbers(purchases.result);

  return purchases;
}

function isPresentAdminFilterValue(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && trimmed !== 'null';
  }
  return true;
}

function shouldSearchOrdersAcrossBrands(options: GetPurchasesOptions): boolean {
  if (options.brand === LE_BUSINESS_TRAVELLER) {
    return false;
  }

  if (options.multiBrand === false) {
    return false;
  }
  if (options.multiBrand === true) {
    return true;
  }

  if (isPresentAdminFilterValue(options.customer_id) || isPresentAdminFilterValue(options.businessId)) {
    return true;
  }

  const { filterBy, filterValue } = options;
  if (!isPresentAdminFilterValue(filterBy)) {
    return false;
  }
  if (filterBy === 'created_at') {
    return true;
  }
  return isPresentAdminFilterValue(filterValue);
}

export async function getPurchases(options: GetPurchasesOptions) {
  const {
    page,
    per_page,
    brand,
    customer_id,
    filterBy,
    filterValue,
    additionalFilter,
    businessId,
    item_type,
    includeSharedOrders,
  } = options;

  const baseURL = getBaseURL();
  const url = `${baseURL}/orders?`;
  const sortBy = additionalFilter?.sortField;
  const sortType = additionalFilter?.sortType;
  const rebookableType = additionalFilter?.rebookableType;
  const newSuppliers = additionalFilter?.newSuppliers;

  // if customer_id is null convert it to undefined
  // because qs only ignores undefined
  const queryParams: GetPurchasesQueryParams = {
    page,
    per_page,
    brand,
    customer_id: customer_id || undefined,
    start_date: options.startDate,
    end_date: options.endDate,
    business_id: businessId || undefined,
    include_shared_orders: includeSharedOrders || undefined,
    include_gift_search: true,
  };

  switch (sortBy) {
    case 'null':
    case null:
    case '':
      break;
    default:
      {
        queryParams['order_by'] = sortBy;
        queryParams['order_direction'] = sortType;
      }
      break;
  }

  switch (rebookableType) {
    case 'null':
    case null:
    case '':
      break;
    default:
      {
        queryParams['rebookable_type'] = rebookableType;
      }
      break;
  }

  switch (filterBy) {
    case 'null':
    case null:
    case undefined:
      break;
    case 'booking_numbers':
      {
        const allValues = filterValue
          .toString()
          .split(',')
          .map((i) => i.trim());
        const id = allValues.toString();
        if (isOrderId(id)) {
          queryParams['orders_id'] = allValues.filter((b) => isOrderId(b)).toString();
        } else {
          queryParams['booking_numbers'] = filterValue.toString();
        }
      }
      break;
    case 'order_number':
      {
        const allValues = filterValue
          .toString()
          .split(',')
          .map((i) => i.trim());
        const id = allValues.toString();
        if (isOrderId(id)) {
          queryParams['orders_id'] = allValues.filter((b) => isOrderId(b)).toString();
        } else {
          queryParams['order_number'] = filterValue.toString();
        }
      }
      break;
    case 'offer_id':
      {
        const allValues = filterValue
          .toString()
          .split(',')
          .map((i) => i.trim());

        queryParams[filterBy] = allValues
          .filter((b) => {
            if (item_type?.includes(ITEM_TYPE_EXPERIENCE) && isExperienceOfferId(b)) {
              return true;
            }

            return isOfferId(b) || isUUID(b);
          })
          .toString();
      }
      break;
    case 'sold_by':
      {
        const allValues = filterValue
          .toString()
          .split(',')
          .map((i) => i.trim());
        queryParams[filterBy] = allValues.filter((b) => isUUID(b)).toString();
      }
      break;
    case 'created_at':
      {
        queryParams.start_date = additionalFilter.startDate;
        queryParams.end_date = additionalFilter.endDate;
      }
      break;
    case 'customer_details':
      {
        queryParams.filterBy = filterBy.toString();
        queryParams.q = filterValue.toString();
      }
      break;
    case 'customer_id':
      {
        queryParams.customer_id = filterValue.toString().trim();
      }
      break;
    case 'external_provider_id':
      {
        const value = filterValue.toString().trim();
        queryParams.external_provider_id = value;
        queryParams.provider_order_id = value;
        queryParams.ibs_member_id = value;
        queryParams.filterBy = 'provider_order_id';
        queryParams.q = value;
      }
      break;
    case 'provider_order_id':
      {
        const value = filterValue.toString().trim();
        queryParams.provider_order_id = value;
        queryParams.external_provider_id = value;
        queryParams.ibs_member_id = value;
        queryParams.filterBy = 'provider_order_id';
        queryParams.q = value;
      }
      break;
    case 'ibs_member_id':
      {
        const value = filterValue.toString().trim();
        queryParams.ibs_member_id = value;
        queryParams.provider_order_id = value;
        queryParams.external_provider_id = value;
        queryParams.filterBy = 'provider_order_id';
        queryParams.q = value;
      }
      break;
    case 'societe_member_id':
      {
        const value = filterValue.toString().trim();
        queryParams.ibs_member_id = value;
        queryParams.provider_order_id = value;
        queryParams.external_provider_id = value;
        queryParams.filterBy = 'provider_order_id';
        queryParams.q = value;
      }
      break;
    case 'traveller_name':
      {
        const travellerName = filterValue.toString().trim();
        if (travellerName) {
          queryParams.traveller_name = travellerName;
        }
      }
      break;
    case 'note_comment':
      {
        const noteComment = filterValue.toString().trim();
        if (noteComment) {
          queryParams.note_comment = noteComment;
        }
      }
      break;
    case 'bedbank_property': {
      if (BedbankService.isBedbankId(filterValue)) {
        queryParams.property = filterValue.toString();
        queryParams.property_field = 'id';
      } else {
        queryParams.property = filterValue.toString();
        queryParams.property_field = 'name';
      }
      break;
    }
    case 'room_mapping_check':
      queryParams[filterBy] = filterValue || true;
      break;
    default:
      {
        queryParams[filterBy] = filterValue.toString();
      }
      break;
  }

  switch (newSuppliers) {
    case null:
    case undefined:
      break;
    case []:
    default:
      {
        queryParams['new_suppliers'] = newSuppliers.join(',');
      }
      break;
  }

  if (options?.offer_type) {
    queryParams.type = options?.offer_type;
  }

  if (options?.item_type) {
    if (Array.isArray(options.item_type)) {
      queryParams.item_types = options.item_type as [string];
    } else {
      queryParams.item_type = options.item_type;
    }
  }

  if (options.accommodationV2Scope) {
    queryParams.accommodation_v2_scope = options.accommodationV2Scope;
  }

  if (shouldSearchOrdersAcrossBrands(options)) {
    queryParams.multi_brand = true;
  }

  const purchases = await request(`${url}${qs.stringify(queryParams)}`, { signal: options.signal });

  // collect all booking numbers per order
  getBookingNumbers(purchases.result);
  restoreOrderId(purchases.result);
  return purchases;
}

export async function getOrder(id_orders, requestOptions?: RequestInit) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/${id_orders}`;
  const response = await request(url, requestOptions);

  return response;
}

export type OrderTransactionStatus = {
  code?: string;
  errors?: Array<{ code?: string }>;
  payment_types?: Array<string>;
};

export async function getOrderTransactionStatus(statusHref: string): Promise<OrderTransactionStatus> {
  const response = await request(`${window.configs.API_HOST}${statusHref}`);
  return response.result;
}

export type CreditReversalOrder = Order.CreditReversalOrder;
export type GetEligibleCreditReversalOrdersResponse = Order.GetEligibleCreditReversalOrdersResponse;

// Local until AP bumps @luxuryescapes/contract-svc-order past 1.26.0, which carries
// Order.GetCreditRefundFrequencyResponse. Swap the alias in then and delete these.
export type CreditRefundFrequency = {
  count: number;
  threshold: number;
  window_days: number;
  blocked: boolean;
};

export type GetCreditRefundFrequencyResponse = {
  status: number;
  message: string;
  result: CreditRefundFrequency;
};

export async function getCreditRefundFrequency(
  userId: string,
  signal?: AbortSignal,
): Promise<GetCreditRefundFrequencyResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/users/${userId}/credit-refund-frequency`;
  return request(url, { method: 'GET', signal });
}

export async function getEligibleCreditReversals(
  userId: string,
  limit: number,
  orderOffset: number,
  orderId?: string,
  signal?: AbortSignal,
): Promise<GetEligibleCreditReversalOrdersResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/users/${userId}/eligible-credit-reversals?${qs.stringify({
    limit,
    orderOffset,
    orderId,
  })}`;
  return request(url, { method: 'GET', signal });
}

export function getOrderCombinedItemIds({ result }) {
  return [].concat(
    result.accommodation_items.map((i) => i.id),
    result.addon_items.map((i) => i.id),
    result.flight_items.map((i) => i.id),
    result.offline_flight_items.map((i) => i.id),
    result.gift_card_items.map((i) => i.id),
    (result.purchase_credit_items ?? []).map((i) => i.id),
    result.bedbank_items.map((i) => i.id),
    result.insurance_items.map((i) => i.id),
    result.tour_items.map((i) => i.id),
    result.merchandise_items.map((i) => i.id),
  );
}

async function getInsuranceClaimStatus(order: App.Order) {
  if (order.insurance_items.length === 0) {
    return;
  }

  // An order contains a unique insurance item
  const insuranceItem = order.insurance_items[0];
  const contractId = insuranceItem.id_contract_number;

  const claim = await fetchInsuranceClaim(contractId);
  return claim.claim_status;
}

function includeInsuranceClaimStatus(order: App.Order, claimStatus: string) {
  if (order.insurance_items.length === 0) {
    return;
  }

  // An order contains a unique insurance item
  order.insurance_items[0] = {
    ...order.insurance_items[0],
    claim_status: claimStatus,
  };
}

export async function getOrderDetail(orderId: string) {
  let orderResponse;
  try {
    orderResponse = await getOrder(orderId);
  } catch (err) {
    if (err.status === 404) {
      throw new Error('Order not found', { cause: err });
    }
    throw new Error(err.message, { cause: err });
  }

  const order = orderResponse.result;

  if (!order.id_orders) {
    order.id_orders = order.id;
  }

  const results = await Promise.allSettled([
    getReservationByOrder(order),
    getPayments(orderId),
    getMerchantFeeDetails(orderId),
    getRefundMeta(orderId),
    getPaymentPlans(orderId),
    getDepositDetails(orderId),
    getReserveForZeroDetails(orderId),
    getInsuranceClaimStatus(order),
  ]);

  const [
    { value: reservations = {} },
    { value: payments = { result: [] } },
    { value: merchantFees = { result: null } },
    { value: refunds = { result: [] } },
    { value: paymentPlans = null },
    { value: depositDetails = null },
    { value: reserveForZeroDetails = null },
    { value: claimStatus = null },
  ] = results.map((result) => (result.status === 'fulfilled' ? result : { value: undefined }));

  order.items.forEach((item) => {
    const reservation = reservations[item.id_items];
    item.fullReservation = reservation || {};
  });
  order.payments = payments.result;
  order.merchant_fees = merchantFees.result?.merchantFeeDetails;
  order.refunds = refunds.result;
  order.payment_plans = paymentPlans;
  order.deposit_details = depositDetails;
  order.reserve_for_zero_details = reserveForZeroDetails;
  includeInsuranceClaimStatus(order, claimStatus);

  try {
    const accommodationVccPromises = order.items
      .filter((item) => canViewVccDetails(auth.getState().user) && item.reservation?.has_vcc)
      .map((item) =>
        ReservationService.getVirtualCreditCardsHistory(item.reservation.id).then((response: ICardResponse) => ({
          ...response.result,
          reservationId: item.reservation.id,
        })),
      );

    // Sabre bedbank hotels will always require VCC payments.
    const sabreBedbankVccPromises = (order.bedbank_items ?? [])
      .filter(
        (item) =>
          canViewVccDetails(auth.getState().user) && item.supplier_code === SuppliersCodes.sabre && item.id_reservation,
      )
      .map((item) =>
        ReservationService.getVirtualCreditCardsHistory(item.id_reservation).then((response: ICardResponse) => ({
          ...response.result,
          reservationId: item.id_reservation,
        })),
      );

    const getVCCPromises = [...accommodationVccPromises, ...sabreBedbankVccPromises];

    if (getVCCPromises.length > 0) {
      const results = await Promise.allSettled(getVCCPromises);
      order.cards = results
        .filter((result): result is PromiseFulfilledResult<ICard> => result.status === 'fulfilled')
        .map((result) => result.value);
    } else {
      order.cards = [];
    }
  } catch (error) {
    console.error('Could not load VCC history', error);
    order.cards = [];
  }

  try {
    const createOrderTransaction = await request(`${window.configs.API_HOST}${order._links.status.href}`);
    order.create_order_transaction = createOrderTransaction.result;
    return order;
  } catch (error) {
    return order;
  }
}

export async function getReservationByOrderAndItem(id_orders, id_items) {
  const baseURL = getBaseURL();
  let data;
  try {
    data = await request(`${baseURL}/orders/${id_orders}/items/${id_items}/reservation`);
  } catch (e) {
    // some orders return 404 for reservation endpoint but have reservation data in the order item
    try {
      data = await request(`${baseURL}/orders/${id_orders}/items/${id_items}`);
      data = { result: data.result.reservation };
    } catch (e) {
      data = { result: [] };
    }
  }

  // This is horrible. It's also the lesser evil.
  data.id_item = id_items;
  data.id_order = id_orders;
  return data;
}

export async function getFlightDetailsByOrderAndItem(
  id_orders,
  id_items,
  options?: { includeTickets?: boolean },
): Promise<App.FlightDetails> {
  const baseURL = getBaseURL();

  const query = qs.stringify(options, { addQueryPrefix: true });
  const data = await request(`${baseURL}/orders/${id_orders}/items/${id_items}/flight-details${query}`);

  return data.result;
}

export async function getRebookStatus(id_orders, id_items) {
  const baseURL = getBaseURL();

  const data = await request(`${baseURL}/orders/${id_orders}/items/${id_items}/rebook/status`);

  return data.result;
}

// N+1 queries; no way will this be a perf problem
export async function getReservationByOrder(order) {
  const promises = [];
  for (const item of order.items) {
    promises.push(getReservationByOrderAndItem(order.id_orders, item.id));
  }
  const out = {};
  const data = await Promise.all(promises);
  for (const datum of data) {
    out[datum.id_item] = datum.result;
  }
  return out;
}

export async function getAddonsByOrder(order) {
  const promises = [];
  const out = {};
  for (const item of order.addon_items) {
    if (typeof out[item.salesforce_id] === 'undefined') {
      out[item.salesforce_id] = null;
      promises.push(
        offersService.getAddon(item.offer_salesforce_id, item.offer_package_salesforce_id, item.salesforce_id),
      );
    }
  }
  const data = await Promise.all(promises);
  for (const datum of data) {
    out[datum.result.id_salesforce_external] = datum.result;
  }
  return out;
}

export async function cancelCustomOfferOrder(orderId: string, itemId: string) {
  const baseURL = getBaseURL();
  const payload = {
    op: OPERATION_CANCEL_CUSTOM_ORDER_ADMIN,
  };
  return postRequest(`${baseURL}/orders/${orderId}/items/${itemId}/cancel`, payload);
}

export async function cancelOrder(orderId, itemId, data, businessId?: string) {
  const baseURL = getBaseURL();
  const payload = {
    ...data,
    amount: parseFloat(data.amount),
    accounting_metadata: data.accounting_metadata.map((x) => ({
      ...x,
      accounting_amount: parseFloat(x.accounting_amount),
      cash_amount: parseFloat(x.cash_amount),
      refund_fee: parseFloat(x.refund_fee),
    })),
  };

  if (businessId) {
    return businessTravellerProxyAdminRefund(businessId, orderId, itemId, 'admin_item_refund', payload);
  }

  return patchRequest(`${baseURL}/orders/${orderId}/items/${itemId}/refund`, payload);
}

export async function refundStatus(orderId, itemId) {
  const baseURL = getBaseURL();

  const data = await request(`${baseURL}/orders/${orderId}/items/${itemId}/refund/status`);

  return data.result;
}

export async function refundFlight(orderId, itemId, data, businessId?: string) {
  const baseURL = getBaseURL();
  const payload = {
    ...data,
    amount: parseFloat(data.amount),
    accounting_metadata: data.accounting_metadata.map((x) => ({
      ...x,
      accounting_amount: parseFloat(x.accounting_amount),
      cash_amount: parseFloat(x.cash_amount),
      refund_fee: parseFloat(x.refund_fee),
    })),
  };

  if (businessId) {
    return businessTravellerProxyAdminRefund(businessId, orderId, itemId, 'admin_flight_refund', payload);
  }

  return patchRequest(`${baseURL}/orders/${orderId}/flights/${itemId}/refund`, payload);
}

interface InsuranceUpdate {
  subscriptionId: string;
  startDate: string;
  endDate: string;
  createdDate: string;
  travellerDetails: Array<App.InsuranceTraveller>;
  coveredAmount?: number;
}

export async function getQuoteUpdate(order: App.Order, updateData: InsuranceUpdate) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/${order.id}/insurance/quote-upgrade`;
  const data = {
    brand: 'luxuryescapes',
    subscription_id: updateData.subscriptionId,
    trip_start_date: updateData.startDate,
    trip_end_date: updateData.endDate,
    sales_channel: 'Sales Agent',
    travellers_details: updateData.travellerDetails,
    ...(updateData.coveredAmount && {
      cover_amount: updateData.coveredAmount,
    }),
  };

  return postRequest(url, data);
}

interface InsuranceUpgradeRequest {
  updateId: string;
  coverAmount: number;
  transactionKey: string;
  total: number;
  subscriptionId?: string;
}

export async function confirmInsuranceUpdate(order: App.Order, updates: InsuranceUpgradeRequest) {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/${order.id}/insurance/upgrade`, {
    brand: 'luxuryescapes',
    ...updates,
  });
}

export async function updateOrderItem(orderId, itemId, data) {
  const baseURL = getBaseURL();

  return putRequest(`${baseURL}/orders/${orderId}/items/${itemId}`, data);
}

export async function getItemCheckInStatus(orderId, itemId) {
  const baseURL = getBaseURL();

  return request(`${baseURL}/orders/${orderId}/items/${itemId}/check-in-details`);
}

export async function getOrderNotes(orderId) {
  const baseURL = getBaseURL();
  const response = await request(`${baseURL}/orders/${orderId}/notes`);
  return response.result;
}

export async function getOrderDatesRequest(orderId: string) {
  const baseURL = getBaseURL();
  const response = await request(`
    ${baseURL}/orders/dates-request?orderId=${orderId}&limit=50`);
  return response.result;
}

export async function getDatesRequests(page = 1, limit = 10) {
  const baseURL = getBaseURL();

  const queryParams = {
    page,
    limit,
    isOutsidePolicy: true,
  };

  const response = await request(`${baseURL}/orders/dates-request?${qs.stringify(queryParams)}`);

  return response;
}

interface GetBaseOrderRefundInfoProps {
  orderId: string;
  itemId: string;
  roomId?: string;
  refundType?: string;
  enriched?: boolean;
  approver?: string;
  selectedReasonKey?: CollectionReasonKey;
  isAfterTourChangeDate?: boolean;
  isAutomatic?: boolean;
  businessId?: string;
}

interface GetOrderRefundInfoProps extends GetBaseOrderRefundInfoProps {
  enriched?: false;
}

interface GetEnrichedOrderRefundInfoProps extends GetBaseOrderRefundInfoProps {
  enriched: true;
}

export async function getOrderRefundInfo(params: GetOrderRefundInfoProps): Promise<RefundMetadata>;
export async function getOrderRefundInfo(params: GetEnrichedOrderRefundInfoProps): Promise<EnrichedRefundMetadata>;
export async function getOrderRefundInfo({
  orderId,
  itemId,
  roomId = undefined,
  refundType = undefined,
  enriched = undefined,
  approver = undefined,
  selectedReasonKey = undefined,
  isAfterTourChangeDate = undefined,
  isAutomatic = undefined,
  businessId = undefined,
}: GetOrderRefundInfoProps | GetEnrichedOrderRefundInfoProps): Promise<RefundMetadata | EnrichedRefundMetadata> {
  const baseURL = getBaseURL();
  const queryParams = qs.stringify({
    room_id: roomId,
    refund_type: refundType,
    enriched,
    approver,
    selectedReasonKey,
    isAfterTourChangeDate,
    isAutomatic,
  });

  if (businessId) {
    const response = await businessTravellerProxyGetAdminRefund(businessId, orderId, itemId, queryParams);
    return response.result;
  }

  const response = await request(`${baseURL}/orders/${orderId}/items/${itemId}/refund?${queryParams}`);
  return response.result;
}

export async function addOrderNote(orderId, data) {
  const baseURL = getBaseURL();

  if (data instanceof FormData) {
    return request(`${baseURL}/orders/${orderId}/notes`, {
      method: 'POST',
      body: data,
    });
  }

  return postRequest(`${baseURL}/orders/${orderId}/notes`, data);
}

export async function deleteOrderNote(orderId, noteId) {
  const baseURL = getBaseURL();
  return patchRequest(`${baseURL}/orders/${orderId}/notes/${noteId}`, {
    op: OPERATION_SOFT_DELETE,
  });
}

export async function gdprRemoval(accountId, data) {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/gdpr-removal/${accountId}`, data);
}

export async function gdprBulkRemoval(data) {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/gdpr-removal`, data);
}

export async function getGdprBulkRemovalJobStatus(jobId) {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/queues/gdpr-bulk-removal/${jobId}`);
}

export async function updateFlightDetails(orderId, itemId, data) {
  const baseURL = getBaseURL();
  return patchRequest(`${baseURL}/orders/${orderId}/items/${itemId}/flight-details`, data);
}

type ExperienceRefundResponse = {
  [experienceId: string]: {
    refundFee?: number;
    refundAmount?: number;
  };
};

export const getExperienceRefund = async (experienceId: string): Promise<ExperienceRefundResponse> => {
  const baseURL = getBaseURL();

  try {
    return await request(`${baseURL}/orders/items/${experienceId}/refund-policies`);
  } catch (error) {
    console.error(`EXPERIENCE REFUND: ${error.message}`);
  }
};

export function getOrdersByFlightItems(property, keys) {
  const baseURL = getBaseURL();

  return postRequest(`${baseURL}/orders/flight-items-by-${property}`, { keys });
}

export type ProductOfflineImpactItem = {
  orderId: string;
  itemId: string;
  packageId: string;
  checkIn: string | null;
};

export type ProductOfflineImpact = {
  awaitingDates: { total: number; sample: Array<ProductOfflineImpactItem> };
  upcomingTrips: { total: number; sample: Array<ProductOfflineImpactItem> };
};

type ProductOfflineImpactQuery = {
  offerId: string;
  packageIds?: Array<string>;
  roomRateIds?: Array<string>;
  roomTypeIds?: Array<string>;
};

export async function getProductOfflineImpact(query: ProductOfflineImpactQuery): Promise<ProductOfflineImpact> {
  const baseURL = getBaseURL();
  const queryParams = {
    offerId: query.offerId,
    packageIds: query.packageIds?.length ? query.packageIds.join(',') : undefined,
    roomRateIds: query.roomRateIds?.length ? query.roomRateIds.join(',') : undefined,
    roomTypeIds: query.roomTypeIds?.length ? query.roomTypeIds.join(',') : undefined,
  };

  const response = await request(
    `${baseURL}/orders/product-offline-impact?${qs.stringify(queryParams, { skipNulls: true })}`,
  );
  return response.result;
}

export async function getItemCountForOffer(offerId) {
  const url = getBaseURL() + `/orders/offer/${offerId}/items-counter/`;

  const orders = await request(`${url}`);
  return orders;
}

export function resendConfirmationEmail(orderId, offerId = null) {
  const url = `${getBaseURL()}/orders/${orderId}/resend`;
  return postRequest(url, { id_offer: offerId });
}

export async function getInvoiceUrl(orderId, noteId, comment) {
  const baseURL = getBaseURL();
  const url = baseURL + `/orders/${orderId}/notes/${noteId}/attachment`;
  const token = await auth.getAccessToken();

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/pdf',
      Authorization: `Bearer ${token}`,
    },
  });
  const body = await response.blob();
  const fileNameHeader = `${comment}-${noteId}.pdf`;
  await fileDownload(body, fileNameHeader);
}

export async function getOrderItemBySubscriptionPeriodId(
  subscriptionPeriodId: string,
): Promise<Order.SubscriptionItem> {
  const baseURL = getBaseURL();
  const response = await request(`${baseURL}/orders/subscription-items?subscription_period_id=${subscriptionPeriodId}`);
  return response;
}

export async function bulkRefundExpiredBnblOrders(data) {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/bulk-refund-expired-bnbl`, data);
}

export async function getOrderItemLogs(orderId: string, itemId: string): Promise<OrderItemLogsResponse> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/${orderId}/items/${itemId}/logs`);
}

export async function getCustomOrderByCustomOffer(customOfferId: string): Promise<Order.Order> {
  const customOrderResponse = await getPurchases({
    item_type: 'custom_offer',
    filterBy: 'offer_id',
    filterValue: customOfferId,
    page: 1,
    per_page: 1,
    brand: 'luxuryescapes',
  });

  if (customOrderResponse && customOrderResponse.result.length === 1) {
    return customOrderResponse.result[0];
  }
  return null;
}

export type CreateOrderAccessInvitationPayload = {
  brand: string;
  invitations: Array<{
    email: string;
    role: typeof OrderAccessRole.GROUP_CUSTOM_OFFER_VIEWER;
  }>;
};

export type CreateOrderAccessInvitationResponse = {
  status: number;
  message: string | null;
  result: Array<App.OrderAccessInvite | App.OrderAccess>;
};

export type UpdateOrderAccessPayload = {
  role?: App.OrderAccessRole;
  status?: App.OrderAccessStatus;
};

export type UpdateOrderAccessResponse = {
  result: App.OrderAccess;
};

export type CancelOrderAccessInvitationResponse = {
  result: App.OrderAccessInvite;
};

export async function createOrderAccessInvitations(
  orderId: string,
  payload: CreateOrderAccessInvitationPayload,
): Promise<CreateOrderAccessInvitationResponse> {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/${orderId}/invitation`, payload);
}

export async function updateOrderAccess(
  orderId: string,
  accessId: string,
  payload: UpdateOrderAccessPayload,
): Promise<UpdateOrderAccessResponse> {
  const baseURL = getBaseURL();
  return patchRequest(`${baseURL}/orders/${orderId}/access/${accessId}`, payload);
}

export async function cancelOrderAccessInvitation(
  orderId: string,
  invitationId: string,
): Promise<CancelOrderAccessInvitationResponse> {
  const baseURL = getBaseURL();
  return patchRequest(`${baseURL}/orders/${orderId}/invitation/${invitationId}/cancel`, {});
}

// TODO: replace these types with @luxuryescapes/contract-svc-order equivalents once available.
export type SeatAddonIdentifier = {
  addonId: string;
  flightId: string;
  travellerId: string;
  seatNumber: string;
};

// TODO: replace these types with @luxuryescapes/contract-svc-order equivalents once available.
export type CancelSeatAddonsResponse = {
  status: number;
  message: string;
  result: {
    cancelled: Array<SeatAddonIdentifier>;
  };
};

// TODO: replace these types with @luxuryescapes/contract-svc-order equivalents once available.
export type AddonRefundAccountingMetadata = {
  source: 'Vendor (TEFH)' | 'Luxury Escapes (TNFH)' | 'Default' | 'Default With Fees' | 'Custom';
  accounting_amount: number;
  cash_amount: number;
  charge_component_key: string;
  refund_fee: number;
};

export type AddonRefundPayload = {
  addonId: string;
  addonType: 'DATE_CHANGE' | 'SEATS' | 'SEATS_AVAILABILITY' | 'CABIN_UPGRADE';
  amount: number;
  refundReason: string;
  accounting_metadata: Array<AddonRefundAccountingMetadata>;
};

export type FlightRefundWithAddonsRequest = {
  transaction_key: string;
  reason: string;
  refund_method: 'Back To Original' | 'LE Credits' | 'Manual';
  comment: string;
  ticket_id: string;
  amount: number;
  currency_code: string;
  send_refund_notification: boolean;
  accounting_metadata: Array<AddonRefundAccountingMetadata>;
  addons: Array<AddonRefundPayload>;
};

export type FlightRefundWithAddonsResponse = {
  status: number;
  message: string;
};

export async function cancelSeatAddons(
  orderId: string,
  itemId: string,
  seats: Array<SeatAddonIdentifier>,
): Promise<CancelSeatAddonsResponse> {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/${orderId}/flights/${itemId}/addons/seats/cancel`, { seats });
}

export async function refundFlightAddons(
  orderId: string,
  itemId: string,
  payload: FlightRefundWithAddonsRequest,
  businessId?: string,
): Promise<FlightRefundWithAddonsResponse> {
  if (businessId) {
    return businessTravellerProxyAdminRefund(
      businessId,
      orderId,
      itemId,
      'admin_flight_refund',
      payload as unknown as Record<string, unknown>,
    ) as Promise<FlightRefundWithAddonsResponse>;
  }
  const baseURL = getBaseURL();
  return patchRequest(`${baseURL}/orders/${orderId}/flights/${itemId}/refund`, payload);
}

export async function getTaxInvoiceDetails(orderId: string) {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/${orderId}/tax-invoice-details`);
}

export async function getRefundRequests(page = 1, limit = 10) {
  const baseURL = getBaseURL();

  const queryParams = {
    page,
    limit,
  };

  const response = await request(`${baseURL}/orders/refund-request?${qs.stringify(queryParams)}`);

  return response;
}

export async function getAirportTransfers(page = 1, limit = 10) {
  const baseURL = getBaseURL();

  const queryParams = {
    page,
    limit,
    sort_by: 'updated_at',
  };

  const response = await request(`${baseURL}/orders/experiences/items/transfers?${qs.stringify(queryParams)}`);

  return response;
}

export async function getRefundRequestsByOrderId(orderId: string): Promise<Array<RefundRequest>> {
  const baseURL = getBaseURL();
  const response = await request(`${baseURL}/orders/refund-request/order/${orderId}`);

  return response.result;
}

export async function getRefundRequestsByOrderItemId(orderId: string, itemId: string): Promise<RefundRequest> {
  const baseURL = getBaseURL();
  const response = await request(`${baseURL}/orders/refund-request/order/${orderId}/item/${itemId}`);

  if (response.result && response.result.length > 0) {
    // Can only have one refund request per item
    return response.result[0];
  }
  return null;
}

export async function updateRefundRequest(refundRequestId, data) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/refund-request/${refundRequestId}`;

  return patchRequest(url, data);
}

export async function getDatesRequestsByOrderItemId(orderId: string, itemId: string): Promise<DateChangeRequest> {
  const baseURL = getBaseURL();
  const response = await request(`${baseURL}/orders/dates-request/order/${orderId}`);

  if (response.result && response.result.length > 0) {
    // Can only have one refund request per item
    return response.result.find((request) => request.is_outside_policy && request.fk_item_id === itemId);
  }
  return null;
}

export async function updateDatesRequest(datesRequestId, data) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/orders/dates-request/${datesRequestId}`;

  return patchRequest(url, data);
}

export async function getOrderItemInsuranceLetterPDF(
  order: App.Order | Order.Order,
  item: App.AnyItem,
  refundRequest: RefundRequest,
  offer?: App.Offer,
): Promise<Blob> {
  const baseURL = getBaseURL();
  const formattedTotal = formatDynamicAmountWithDollarType(item.total, order.currency_code, order.region_code);

  const letterDetails: any = {
    customerFullName: order.customer_full_name,
    duration: (item.number_of_nights + item.extra_nights).toString(),
    purchaseDate: order.created_at,
    propertyName: item.offer.name,
    sellPrice: formattedTotal,
    refundRequestDate: refundRequest?.created_at ?? dateNow(),
  };

  if (offer) {
    letterDetails.checkIn = formatDateISO(item.reservation?.check_in) ?? '';
    letterDetails.checkOut = formatDateISO(item.reservation?.check_out) ?? '';
    letterDetails.vendorName = offer?.vendor_name ?? item.offer.location ?? '';
  } else {
    letterDetails.checkIn = formatDateISO(item.checkIn);
    letterDetails.checkOut = formatDateISO(item.checkOut);
    letterDetails.vendorName = item.offer.property.location; // TODO find Property name
  }

  const url = `${baseURL}/pdf/cancellation-insurance-letter`;

  return await request(url, {
    method: 'POST',
    body: JSON.stringify(letterDetails),
    headers: { 'Content-Type': 'application/json' },
    download: true,
  });
}

export async function getOrdersByIds(id_orders: Array<string>, brand: string) {
  const baseURL = getBaseURL();

  const queryParams = {
    orders_id: id_orders.join(','),
    brand: brand,
  };

  const url = `${baseURL}/orders?${qs.stringify(queryParams)}`;

  const orders = await request(url);
  return orders.result;
}

export async function convertAddonsToExperiences(offerId: string) {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/experiences/migrate-complimentary-experiences`, { offerIds: [offerId] });
}

export async function createComplimentaryExperience(orderId: string, data) {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/${orderId}/create-complimentary-experiences`, data);
}

export async function checkFraud(orderId: string): Promise<{ score: number; fraudDesc: string }> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/check-fraud/${orderId}`);
}

export async function getBedbankOrderByBookingNumber(bookingNumber: string): Promise<Array<Order.Order>> {
  const baseURL = getBaseURL();
  const orders = await request(`${baseURL}/orders/search?searchId=${bookingNumber}`);
  return orders.result;
}

export async function backFillGstForOrder(orderId: string): Promise<void> {
  const baseURL = getBaseURL();
  await postRequest(`${baseURL}/orders/${orderId}/backfill-gst`, {});
}

/**
 * Create a bulk VCC replacement job.
 * POST /api/orders/vcc/bulk-replace
 */
export async function createBulkVccReplaceJob(
  records: Array<Order.BulkVccReplaceRecord>,
): Promise<Order.BulkVccReplaceCreateResponse> {
  const baseURL = getBaseURL();
  return postRequest(`${baseURL}/orders/vcc/bulk-replace`, { records });
}

/**
 * Get the status of a bulk VCC replacement job.
 * GET /api/orders/vcc/bulk-replace/:jobId
 */
export async function getBulkVccReplaceJobStatus(jobId: string): Promise<Order.BulkVccReplaceStatusResponse> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/vcc/bulk-replace/${jobId}`);
}

export function updateCruiseDetails(orderId: string, itemId: string) {
  const baseURL = getBaseURL();
  return putRequest(`${baseURL}/orders/${orderId}/items/${itemId}/cruise-details`, {});
}

// TODO: Replace OrderEditLogField, OrderEditLogEntry, and GetOrderEditLogsResponse
// with types from @luxuryescapes/contract-svc-order once the contract is published.
// These are kept in sync with Order.OrderEditLogField, Order.OrderEditLog, and
// Order.GetOrderEditLogsResponse in svc-order/src/contract/types/order.d.ts.
export type OrderEditLogField = 'fraud_status' | 'utm_medium' | 'fk_purchaser_id' | 'status';

export interface OrderEditLogEntry {
  id_order_edit_logs: string;
  fk_order_id: string;
  field_name: OrderEditLogField;
  previous_value: string | null;
  new_value: string | null;
  changed_by_id: string;
  source: string;
  created_at: string;
}

export interface GetOrderEditLogsResponse {
  message: null;
  status: number;
  result: Array<OrderEditLogEntry>;
  count: number;
  total: number;
}

export async function getOrderEditLogs(orderId: string, page = 1, limit = 20): Promise<GetOrderEditLogsResponse> {
  const baseURL = getBaseURL();
  return request(`${baseURL}/orders/${orderId}/edit-logs?page=${page}&limit=${limit}`);
}

export type AccommodationV2ReconciliationCheckResult = 'pass' | 'warn' | 'fail';

export interface AccommodationV2ReconciliationCheck {
  key: string;
  label: string;
  result: AccommodationV2ReconciliationCheckResult;
  detail: string;
}

export interface AccommodationV2ShadowReconciliation {
  itemId: string;
  legacyItemType: string | null;
  legacyItemId: string | null;
  /** The legacy item's current `updated_at` in epoch ms, read when the checks ran. */
  legacyItemVersion: number | null;
  bookingId: string | null;
  checks: Array<AccommodationV2ReconciliationCheck>;
}

/** svc-order's on-demand shim linkage and lifecycle checks for every shadow row on an order. */
export async function getAccommodationV2Reconciliation(
  orderId: string,
  options?: { signal?: AbortSignal },
): Promise<Array<AccommodationV2ShadowReconciliation>> {
  const { result } = await request(`${getBaseURL()}/orders/${orderId}/accommodation-v2/reconciliation`, {
    signal: options?.signal,
  });
  return result.items as Array<AccommodationV2ShadowReconciliation>;
}
